import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Achievements from "../../component/profile/view/achievements";
import BasicDetails from "../../component/profile/view/basicDetails";
import FighterDetails from "../../component/profile/view/fighterDetails";
import FightHistory from "../../component/profile/view/fightHistory";
import Sponsorships from "../../component/profile/view/sponsorships";
import VideoUrl from "../../component/profile/view/videoUrl";
import YouTubeLinks from "../../component/profile/view/youtubeLinks";
import { UpdateProfileFeature } from "../../features/profile/updateProfileFeature";
import {
  createProfileView,
  getAllRatings,
  getProfileById,
  getProfileUser,
  verifyProfile,
} from "../../services/api";
import { LoginResponse, Profile, RatingResponseDto } from "../../types/types";
import { ReportAbusePopup } from "../abuseReport/reportAbusePopup";
import FightScores from "../../component/profile/view/fightScores";
import Ratings from "../../component/profile/view/ratings";
import { PostSection } from "../../component/profile/view/postSection";

const PublicProfile = () => {
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get("profileId");
  const [userId, setUserId] = useState<string | null>(null);
  const [isUpdatePanelOpen, setIsUpdatePanelOpen] = useState(false);
  const [member, setMember] = useState<Profile | null>(null);
  const [ratingData, setRatingData] = useState<RatingResponseDto[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRatingElements, setTotalRatingElements] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("basic-details");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<LoginResponse>();
  const [isRatingsLoading, setIsRatingsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const isOwnProfile = Boolean(
    (!profileId &&
      member &&
      loggedInUser &&
      (member.id === loggedInUser.id ||
        member.username === loggedInUser.username)) ||
    (profileId && userId && profileId === userId) ||
    (member &&
      loggedInUser &&
      (member.id === loggedInUser.id ||
        member.username === loggedInUser.username)),
  );
  const isRatingsLoadingRef = useRef(false);
  const loadedRatingsProfileIdRef = useRef<string | null>(null);

  const { t, i18n } = useTranslation("publicProfile");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const handleScrollCombined = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const containerTop = container.scrollTop;
    const containerHeight = container.clientHeight;

    let mostVisibleSection = tabs[0].id;
    let maxVisibleHeight = 0;

    tabs.forEach((tab) => {
      const sectionElement = sectionRefs.current[tab.id];
      if (sectionElement) {
        const sectionTop = sectionElement.offsetTop - container.offsetTop;
        const sectionBottom = sectionTop + sectionElement.offsetHeight;
        const visibleTop = Math.max(containerTop, sectionTop);
        const visibleBottom = Math.min(
          containerTop + containerHeight,
          sectionBottom,
        );
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          mostVisibleSection = tab.id;
        }
      }
    });

    if (mostVisibleSection !== activeTab) {
      setActiveTab(mostVisibleSection);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScrollCombined);
    return () => container.removeEventListener("scroll", handleScrollCombined);
  }, [activeTab, currentPage, totalPages, isRatingsLoading]);

  useEffect(() => {
    let cancelled = false;

    const fetchMember = async () => {
      try {
        const userJson = localStorage.getItem("loginResponse");
        const user = userJson ? JSON.parse(userJson) : null;
        setLoggedInUser(user);

        const targetId =
          profileId || user?.id?.toString() || user?.profileId?.toString();
        if (!targetId) return;

        setLoading(true);
        setError(null);
        setMember(null);
        setActiveTab("basic-details");

        if (user?.username) {
          setUserId(user.id?.toString?.() ?? null);
          const res = await getProfileUser(targetId, user.username);
          if (!cancelled) setMember(res);
        } else {
          const res = await getProfileById(targetId);
          if (cancelled) return;

          if (res.success && res.data) {
            setMember(res.data);
          } else {
            setError("Profile not found");
          }
        }
      } catch (err) {
        if (!cancelled) setError("Profile not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMember();

    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const loadRatingsAPI = async (page = 0) => {
    if (isRatingsLoadingRef.current || !profileId) return;

    try {
      isRatingsLoadingRef.current = true;
      setIsRatingsLoading(true);

      const res = await getAllRatings(profileId, page, 10);

      setRatingData(res.content ?? []);
      loadedRatingsProfileIdRef.current = profileId;

      setTotalPages(res.totalPages ?? 1);
      setTotalRatingElements(res.totalElements ?? 0);
      setCurrentPage(res.number ?? page);
    } catch (e) {
      console.error("Failed to load ratings", e);
    } finally {
      isRatingsLoadingRef.current = false;
      setIsRatingsLoading(false);
    }
  };

  useEffect(() => {
    if (!profileId || loadedRatingsProfileIdRef.current === profileId) return;

    setRatingData([]);
    setCurrentPage(0);
    setTotalPages(1);
    setTotalRatingElements(0);
    loadRatingsAPI(0);
  }, [profileId]);

  const RatingsLoader = () => (
    <div className="w-full py-4 flex justify-center items-center">
      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500"></div>
    </div>
  );

  const scrollToSection = (sectionId: string) => {
    if (
      sectionId === "ratings" &&
      loadedRatingsProfileIdRef.current !== profileId
    ) {
      loadRatingsAPI(0);
    }

    const sectionElement = sectionRefs.current[sectionId];
    if (sectionElement && scrollContainerRef.current) {
      const sectionTop =
        sectionElement.offsetTop - scrollContainerRef.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: sectionTop,
        behavior: "smooth",
      });
    }

    setActiveTab(sectionId);
  };

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  if (error || !member) {
    return <div>{error || t("profile_not_found")}</div>;
  }
  const tabs = [
    {
      id: "basic-details",
      title: t("basic_details").toUpperCase(),
      content: <BasicDetails member={member} showCopyButton={true} />,
    },
  ];

  if (member.profileType === "MANAGER") {
    tabs.push({
      id: "ratings",
      title: t("ratings").toUpperCase(),
      content: (
        <>
          <Ratings
            member={member}
            ratings={ratingData}
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalRatingElements}
            isLoading={isRatingsLoading}
            onPageChange={loadRatingsAPI}
          />
          {isRatingsLoading && <RatingsLoader />}
        </>
      ),
    });
  } else if (member.fighter) {
    tabs.push(
      {
        id: "fighter-details",
        title: t("fighter_details").toUpperCase(),
        content: <FighterDetails member={member} />,
      },
      {
        id: "fight-history",
        title: t("fight_history").toUpperCase(),
        content: <FightHistory member={member} />,
      },
      {
        id: "achievements",
        title: t("achievements").toUpperCase(),
        content: <Achievements member={member} />,
      },
      {
        id: "video-url",
        title: t("video_url").toUpperCase(),
        content: <VideoUrl member={member} />,
      },
      {
        id: "youtube-links",
        title: t("youtube_links").toUpperCase(),
        content: <YouTubeLinks member={member} />,
      },
      {
        id: "sponserships",
        title: t("sponserships").toUpperCase(),
        content: <Sponsorships member={member} />,
      },
      {
        id: "ratings",
        title: t("ratings").toUpperCase(),
        content: (
          <>
            <Ratings
              member={member}
              ratings={ratingData}
              currentPage={currentPage}
              totalPages={totalPages}
              totalElements={totalRatingElements}
              isLoading={isRatingsLoading}
              onPageChange={loadRatingsAPI}
            />
            {isRatingsLoading && <RatingsLoader />}
          </>
        ),
      },
    );
  }

  tabs.push({
    id: "posts",
    title: t("posts").toUpperCase(),
    content: <PostSection profileId={profileId ?? member.id} />,
  });

  return (
    <div className="mx-auto overflow-hidden h-[90vh] relative">
      <div className="grid grid-cols-12 gap-6">
        <div
          ref={scrollContainerRef}
          className="col-span-9 p-6 bg-content1 overflow-y-auto max-h-[90vh]"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .col-span-9::-webkit-scrollbar {
                  display: none;
                }
              `,
            }}
          />
          {tabs.map((tab) => (
            <div
              key={tab.id}
              ref={(el) => {
                sectionRefs.current[tab.id] = el;
              }}
              id={tab.id}
            >
              {tab.content}
            </div>
          ))}
        </div>

        <div className="col-span-3 p-4 mt-4 flex flex-col h-full space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`w-full h-8 font-medium justify-start text-left px-3 rounded-md text-xs transition-all duration-150 ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                  : "bg-gray-100 text-slate-700 hover:bg-gray-200"
              }`}
              onClick={() => scrollToSection(tab.id)}
            >
              {tab.title}
            </button>
          ))}

          {member?.id !== loggedInUser?.id && (
            <button
              disabled={member?.profileStatus !== "ACTIVE" || isVerifying}
              onClick={async () => {
                setIsVerifying(true);

                try {
                  const newVerifiedStatus = !member?.verified;
                  const updatedProfile = await verifyProfile(
                    member.id,
                    newVerifiedStatus,
                    loggedInUser?.username || "",
                  );

                  setMember(updatedProfile);
                } catch (err) {
                  console.error("Verification toggle failed:", err);
                } finally {
                  setIsVerifying(false);
                }
              }}
              className={`w-full h-8 mt-2 px-3 rounded-md text-xs text-left font-medium transition-colors duration-200 
        ${
          member?.verified
            ? "bg-green-500 text-white hover:bg-green-600"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }
        ${
          member?.profileStatus !== "ACTIVE"
            ? "opacity-50 cursor-not-allowed"
            : ""
        }
      `}
            >
              {isVerifying ? (
                <span className="flex items-center justify-center w-full">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </span>
              ) : member?.verified ? (
                t("verified_button").toUpperCase()
              ) : (
                t("unverified_button").toUpperCase()
              )}
            </button>
          )}

          {loggedInUser?.username &&
            member.username !== loggedInUser?.username &&
            loggedInUser.profileType !== "ADMIN" && (
              <>
                {member?.reported === true ? (
                  <button
                    disabled
                    className="w-full h-8 mt-2 px-3 rounded-md font-semibold text-xs bg-gray-300 text-gray-700 cursor-not-allowed text-left"
                  >
                    {t("reported").toUpperCase()}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowReportPopup(true)}
                    className="w-full h-8 mt-2 px-3 rounded-md text-xs bg-rose-500 font-semibold text-white hover:bg-rose-600 flex items-center gap-1 text-left"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    {t("report_abuse").toUpperCase()}
                  </button>
                )}
                {member?.id && (
                  <ReportAbusePopup
                    isOpen={showReportPopup}
                    onClose={() => setShowReportPopup(false)}
                    onSubmit={(reportType, reportDescription) => {
                      console.log("Report submitted (Profile):", {
                        profileId: member.id,
                        reportType,
                        reportDescription,
                      });
                      setMember((prev) =>
                        prev && prev.id === member.id
                          ? { ...prev, reported: true }
                          : prev,
                      );
                      setShowReportPopup(false);
                    }}
                    cardId={member.id.toString()}
                    type="PROFILE"
                    reportedTo={member.id}
                  />
                )}
              </>
            )}
          <div className="flex-grow" />
          {isOwnProfile && (
            <button
              className="mt-auto w-full h-8 justify-start text-left self-end bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium px-4 rounded-md shadow-sm"
              onClick={() => setIsUpdatePanelOpen(true)}
            >
              {t("edit_profile").toUpperCase()}
            </button>
          )}
        </div>
      </div>

      {isUpdatePanelOpen && isOwnProfile && member && (
        <div className="fixed inset-0 z-50 flex items-start justify-center">
          <div className="bg-white w-full mx-auto rounded-md shadow-xl overflow-y-auto max-h-[90vh]">
            <UpdateProfileFeature
              member={member}
              onClose={() => setIsUpdatePanelOpen(false)}
              onSubmitSuccess={async () => {
                setIsUpdatePanelOpen(false);
                try {
                  const userJson = localStorage.getItem("loginResponse");
                  const user = userJson ? JSON.parse(userJson) : null;
                  const targetId = profileId || user?.id;

                  if (targetId && user?.username) {
                    const res = await getProfileUser(targetId, user.username);
                    setMember(res);
                  }
                } catch (err) {
                  console.error("Failed to refresh profile data:", err);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfile;
